import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Plus, Trash, PencilSimple, X, FloppyDisk } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";
const EMPTY = {
  title: "", client: "", industry: "", country: "", cover_image: "",
  summary: "", challenge: "", approach: "", resultsText: "", metricsText: "",
  quote: "", quote_author: "", published: true,
};

const toForm = (cs) => ({
  ...EMPTY, ...cs,
  resultsText: (cs.results || []).join("\n"),
  metricsText: (cs.metrics || []).map((m) => `${m.value} | ${m.label}`).join("\n"),
});

const toPayload = (f) => ({
  title: f.title, client: f.client, industry: f.industry, country: f.country,
  cover_image: f.cover_image, summary: f.summary, challenge: f.challenge, approach: f.approach,
  results: f.resultsText.split("\n").map((s) => s.trim()).filter(Boolean),
  metrics: f.metricsText.split("\n").map((s) => s.trim()).filter(Boolean).map((line) => {
    const [value, ...rest] = line.split("|");
    return { value: (value || "").trim(), label: rest.join("|").trim() };
  }),
  quote: f.quote, quote_author: f.quote_author, published: f.published,
});

export function CaseStudiesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/case-studies").then((r) => setItems(r.data)).catch((e) => console.error("Failed to load case studies:", e));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) { await api.put(`/admin/case-studies/${editId}`, toPayload(form)); toast.success("Case study updated"); }
      else { await api.post("/admin/case-studies", toPayload(form)); toast.success("Case study published"); }
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this case study permanently?")) return;
    try { await api.delete(`/admin/case-studies/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-case-studies-manager">
      <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="case-study-form">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-ink">{editId ? "Edit case study" : "New case study"}</h3>
          {editId && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-slate-500 hover:text-ink"><X size={14} /> Cancel</button>}
        </div>
        <input required placeholder="Title" value={form.title} onChange={set("title")} data-testid="cs-title" className={field} />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Client name" value={form.client} onChange={set("client")} data-testid="cs-client" className={field} />
          <input placeholder="Country" value={form.country} onChange={set("country")} className={field} />
        </div>
        <input placeholder="Industry" value={form.industry} onChange={set("industry")} className={field} />
        <input placeholder="Cover image URL (optional)" value={form.cover_image} onChange={set("cover_image")} className={field} />
        <ImageUpload value={form.cover_image} onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))} label="Upload cover image" testid="cs-cover-upload" />
        <textarea required rows={2} placeholder="Summary (one-liner shown on cards)" value={form.summary} onChange={set("summary")} data-testid="cs-summary" className={field} />
        <textarea rows={3} placeholder="The challenge" value={form.challenge} onChange={set("challenge")} className={field} />
        <textarea rows={3} placeholder="Our approach" value={form.approach} onChange={set("approach")} className={field} />
        <textarea rows={3} placeholder="Results — one per line" value={form.resultsText} onChange={set("resultsText")} className={field} />
        <textarea rows={2} placeholder={"Metrics — one per line as: value | label\ne.g. 38% | Lower unit cost"} value={form.metricsText} onChange={set("metricsText")} className={field} />
        <textarea rows={2} placeholder="Client quote" value={form.quote} onChange={set("quote")} className={field} />
        <input placeholder="Quote author (e.g. Jane D., Founder — Brand Co.)" value={form.quote_author} onChange={set("quote_author")} className={field} />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
        </label>
        <p className="text-[11px] text-slate-400">French & Swahili translations are generated automatically after saving.</p>
        <button type="submit" data-testid="cs-submit" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800">
          {editId ? <><FloppyDisk size={16} weight="bold" /> Save changes</> : <><Plus size={16} weight="bold" /> Publish</>}
        </button>
      </form>

      <div className="space-y-3">
        <h3 className="font-heading text-lg font-semibold text-ink">Case studies ({items.length})</h3>
        {items.map((cs) => (
          <div key={cs.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-cs-${cs.id}`}>
            <div>
              <p className="font-heading text-sm font-semibold text-ink">{cs.title}</p>
              <p className="text-xs text-slate-400">{cs.client} · {cs.country}{cs.published === false && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Draft</span>}</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { setEditId(cs.id); setForm(toForm(cs)); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`edit-cs-${cs.id}`} className="text-slate-400 hover:text-teal"><PencilSimple size={18} /></button>
              <button onClick={() => del(cs.id)} data-testid={`delete-cs-${cs.id}`} className="text-slate-400 hover:text-red-600"><Trash size={18} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No case studies yet.</p>}
      </div>
    </div>
  );
}
